import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const DonorBenefits = () => {
  // ── Key Benefits data ──
  const benefits = [
    {
      icon: "📜",
      title: "80G Tax Exemption",
      desc: "All donations to Venkatadri Seva Trust are eligible for 80G tax exemption under the Income Tax Act, saving you up to 50% on your taxable income.",
    },
    {
      icon: "🏅",
      title: "Recognition & Acknowledgment",
      desc: "Receive a formal certificate of appreciation, recognition on our website and social media (with your consent), and an annual impact report.",
    },
    {
      icon: "📊",
      title: "Complete Transparency",
      desc: "We provide detailed receipts, quarterly impact reports, and regular updates on how your contribution is being utilised—right down to the project level.",
    },
    {
      icon: "🤝",
      title: "CSR Compliance",
      desc: "Corporate donors receive CSR certificates and compliance reports as per Schedule VII of the Companies Act, 2013, making it easy for your company to meet CSR obligations.",
    },
    {
      icon: "💝",
      title: "Emotional Fulfillment",
      desc: "Experience the joy of making a tangible difference in someone's life. Connect with the communities you support through newsletters, stories, and site visits.",
    },
    {
      icon: "🌟",
      title: "Exclusive Donor Community",
      desc: "Join a community of like-minded changemakers. Get invitations to donor meet-ups, webinars, and special events where you can interact with beneficiaries and project leads.",
    },
  ];

  // ── Impact Stats ──
  const stats = [
    { number: "₹5 Cr+", label: "Funds Utilised" },
    { number: "15,000+", label: "Lives Impacted" },
    { number: "98%", label: "Transparency Rating" },
    { number: "2,400+", label: "Active Donors" },
  ];

  // ── How Donations Help ──
  const donationImpacts = [
    { icon: "🎓", title: "Education", desc: "Scholarships, books, and digital learning for 4,500+ students." },
    { icon: "🏥", title: "Healthcare", desc: "Medical camps, surgeries, and medicines for 5,800+ patients." },
    { icon: "👵", title: "Elder Care", desc: "Essential supplies and emotional support for 2,400+ senior citizens." },
    { icon: "🌳", title: "Environment", desc: "25,000+ trees planted and 120+ water bodies revived." },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="benefits-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-gift mr-2"></i> Donor Benefits
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Why Donate to Venkatadri Seva Trust?
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Discover the rewards of giving—from tax savings to the joy of transforming lives
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Giving That Gives Back
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              When you donate to Venkatadri Seva Trust, you're not just supporting a cause—you're becoming a
              part of a movement that uplifts thousands of lives. We believe in creating a win-win relationship
              with our donors, where your generosity is acknowledged, your trust is respected, and your impact
              is visible.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              From tax benefits and transparency to emotional rewards and community recognition, here's why
              thousands of individuals and corporations choose to partner with us.
            </p>
          </div>
        </div>
      </section>

      {/* ========== KEY BENEFITS ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Key Benefits for Donors</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Every donation comes with meaningful advantages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{benefit.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW YOUR DONATION HELPS ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">How Your Donation Helps</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Real impact across our key focus areas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {donationImpacts.map((impact, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center"
              >
                <div className="text-5xl mb-3">{impact.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800">{impact.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{impact.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMPACT STATS ========== */}
      {/* <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Transparency & Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Donor Testimonials</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-lg bg-gray-50 border-l-4 border-primary">
                <p className="text-gray-700 text-sm italic">
                  "I've been donating to Venkatadri Seva Trust for 3 years. The tax benefits are great, but what
                  keeps me coming back is the regular impact reports—they show exactly where my money goes.
                  I've even visited a project site!"
                </p>
                <p className="mt-3 font-semibold text-gray-800">— Priya Sharma</p>
                <p className="text-xs text-gray-500">Monthly Donor</p>
              </div>
              <div className="p-6 rounded-lg bg-gray-50 border-l-4 border-primary">
                <p className="text-gray-700 text-sm italic">
                  "As a CSR partner, we needed a reliable NGO with proper documentation and measurable outcomes.
                  Venkatadri Seva Trust exceeded our expectations. Their CSR reports are detailed and their
                  team is very professional."
                </p>
                <p className="mt-3 font-semibold text-gray-800">— Rajesh Patel</p>
                <p className="text-xs text-gray-500">Corporate Donor</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CALL TO ACTION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Make a Difference?</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 mb-8">
              Join thousands of generous donors who are transforming lives every day. Your contribution—big or
              small—creates lasting change.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Donate Now
              </a>

            </div>
            <p className="text-yellow-100/70 text-sm mt-6">
              * All donations are eligible for 80G tax exemption. We provide instant receipts and compliance reports.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default DonorBenefits;